import { motion } from 'framer-motion'
import {
  ComponentProps,
  ElementRef,
  FC,
  type JSX,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import AccordionList from '../../components/AccordionList'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import Input from '../../components/Input'
import LoadingText from '../../components/LoadingText'
import ReviewStars from '../../components/ReviewStars'
import Select from '../../components/Select'
import TagList from '../../components/TagList'
import { useWindowSize } from '../../hooks/useWindowSize'
import {
  GrouppedOptionsByName,
  type LoadableComponentProps,
  MappedProductOptions,
  OptionType,
  type ProductDataForTags,
  ProductVariant,
} from '../../types'
import { BuyNowButton } from './BuyNowButton'
import { ShopPayCheckoutButton, type ShopPayCheckoutButtonProps } from './ShopPayCheckoutButton'

export type ProductControlsProps = {
  currentVariant: ProductVariant | null
  variants: ProductVariant[]
  isProductAvailable: boolean
  isAddButtonDisabled: boolean
  title: string
  quantity: number
  onCountChange: (quantity: number) => void
  onVariantChange: (selectedVariantId: string) => void
  onSelectedOptionChange: (options: GrouppedOptionsByName, type: OptionType) => void
  onAddItemToCheckout: () => void
  onBuyNow?: () => void
  buyNowButtonText?: string
  shopPay?: ShopPayCheckoutButtonProps | null
  onSubscribe: (email: string, newsletter: boolean) => void
  shippingDetails?: Array<{
    text: string
    icon?: string | null
  }>
  productInfo: {
    description: (JSX.Element | null)[] | string
    accordion: ComponentProps<typeof AccordionList>['panels']
  }
  reviews?: {
    reviewsCount?: number
    reviewsScore?: number
  }
  reviewsText: string
  addToCartButtonText: string
  emailNotificationDescription: (JSX.Element | null)[]
  itemIsOutOfStockButtonText: string
  itemIsOutOfStockFormSuccess: boolean
  itemIsOutOfStockFormSuccessMessage: string | null
  itemIsOutOfStockEmailNotificationInput: string
  itemIsOutOfStockEmailNotificationNewsletterCheckboxText: string
  itemIsOutOfStockEmailNotificationButtonText: string
  accessibility?: {
    price: string
    compareAtPrice: string
  }
  helperText?: string
  productOptions: MappedProductOptions
  tagListOptions: ProductDataForTags[]
  tagListSelectedOptions: { [key: string]: string }
  selectSelectedOptions: { [key: string]: string }
}

const filterObject = (obj: object, callback: (val: unknown, key: string) => boolean) => {
  return Object.fromEntries(Object.entries(obj).filter(([key, val]) => callback(val, key)))
}

export const ProductControls: FC<LoadableComponentProps<ProductControlsProps>> = ({
  data,
  loading,
}) => {
  const [subscribeEmail, setSubscribeEmail] = useState('')
  const [enableNewsletter, setEnableNewsletter] = useState(false)
  const [mounted, setMounted] = useState(false)
  const { isDesktop, windowSize } = useWindowSize()

  const buyBarRef = useRef<ElementRef<'div'>>(null)
  const showStickyBuyBar = useMemo(
    () => mounted && windowSize.width < 1024,
    [mounted, windowSize.width],
  )

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!showStickyBuyBar) return
    let lastScroll = window.scrollY
    const observedButton = document.querySelector('#shopButton')
    const fixedButton = buyBarRef.current
    const fixedButtonClasses = fixedButton?.className

    if (!observedButton) return

    const callback = (entries: IntersectionObserverEntry[]) => {
      const currentScroll = window.scrollY
      const scrolledDown = currentScroll > lastScroll
      lastScroll = currentScroll

      entries.forEach(entry => {
        if (!entry.isIntersecting && scrolledDown) {
          actionWhenScrolledDown()
        } else if (entry.isIntersecting && !scrolledDown) {
          actionWhenScrolledUp()
        }
      })
    }

    const observer = new IntersectionObserver(callback, {
      threshold: 0,
    })
    observer.observe(observedButton)

    function actionWhenScrolledDown() {
      if (!fixedButton) return
      fixedButton.style.display = 'flex'
      fixedButton.className = fixedButtonClasses + ' openDownToUp'
    }

    function actionWhenScrolledUp() {
      if (!fixedButton) return
      fixedButton.className = fixedButtonClasses + ' closeDownToUp'
      setTimeout(() => {
        fixedButton.style.display = 'none'
      }, 300)
    }

    return () => {
      observer.disconnect()
    }
  }, [showStickyBuyBar])

  useLayoutEffect(() => {
    if (showStickyBuyBar && buyBarRef.current != null) {
      const { height } = buyBarRef.current.getBoundingClientRect()

      document.body.style.paddingBottom = `${height}px`
    }

    return () => {
      document.body.style.paddingBottom = '0'
    }
  }, [showStickyBuyBar])

  const { optionsForSelect, optionsForTagList: initialTagListOptions } = loading
    ? { optionsForSelect: [], optionsForTagList: [] }
    : data.productOptions

  const { tags, tagStartValue, onChangeTagValue } = useMemo(() => {
    if (loading) return { tags: [], tagStartValue: '', onChangeTagValue: () => {} }
    const isTagsWithSelect = optionsForSelect.length > 0

    const tagOptions = data.tagListOptions.length > 0 ? data.tagListOptions : initialTagListOptions
    const tagsConfig = {
      tags: isTagsWithSelect
        ? tagOptions.map(({ title, variants }) => ({
            text: title,
            tagValue: JSON.stringify(variants),
            dataTestId: 'product-variant-tag',
          }))
        : data.variants.length > 1
          ? data.variants.map(({ id, title }) => ({
              text: title,
              tagValue: id,
              dataTestId: 'product-variant-tag',
            }))
          : [],
      tagStartValue: isTagsWithSelect
        ? JSON.stringify(
            Object.keys(data.tagListSelectedOptions).length > 0
              ? data.tagListSelectedOptions
              : filterObject(data.currentVariant?.selectedOptions ?? {}, (_, key) =>
                  Object.keys(data.tagListOptions[0]?.variants ?? []).includes(key),
                ),
          )
        : (data.currentVariant?.id ?? ''),
      onChangeTagValue: isTagsWithSelect
        ? (value: string) => {
            data.onSelectedOptionChange(JSON.parse(value), 'tags')
          }
        : data.onVariantChange,
    }

    return tagsConfig
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsForSelect, data?.tagListSelectedOptions, data?.tagListOptions, data?.currentVariant])

  const scrollToReviews = useCallback(() => {
    const reviewsPosition = document.getElementById('product-reviews')?.offsetTop || 0
    const scrollPosition = Math.max(0, reviewsPosition - 50)

    window.scrollTo(0, scrollPosition)
    window.history.replaceState(null, '', '#product-reviews')
  }, [])

  return (
    <div className='flex flex-col gap-10'>
      <div className='flex flex-col gap-5 lg:gap-8'>
        <h2 className='h2'>{loading ? <LoadingText className='w-[70%]' /> : data.title}</h2>

        {!loading && data.reviews?.reviewsScore != null && (
          <div className='flex items-center gap-2 xl:gap-4'>
            {/* Desktp Review */}
            <div className='hidden h-full items-center gap-2 xl:flex'>
              <ReviewStars score={data.reviews.reviewsScore ?? 0} starSize={20} />

              <p className='lg'>{data.reviews.reviewsScore}</p>

              <button
                className='underline-animated'
                onClick={e => {
                  e.preventDefault()
                  scrollToReviews()
                }}
              >
                <p className='lg text-darkgray'>
                  {`${data.reviews.reviewsCount ?? 0} ${data.reviewsText}`.trim()}
                </p>
              </button>
            </div>
            {/* Mobile Review */}
            <ReviewStars
              className='xl:hidden'
              score={data.reviews.reviewsScore ?? 0}
              starSize={20}
            />

            <button
              className='xl:hidden'
              onClick={e => {
                e.preventDefault()
                scrollToReviews()
              }}
            >
              <p className='lg text-black'>
                {`${data.reviews.reviewsCount ?? 0} ${data.reviewsText}`.trim()}
              </p>
            </button>
          </div>
        )}

        {loading ? (
          <>
            <LoadingText className='w-[30%]' />
            <div className='flex flex-col gap-4'>
              <LoadingText className='w-full' />
              <LoadingText className='w-full' />
              <LoadingText className='w-full' />
              <LoadingText className='w-full' />
            </div>
          </>
        ) : (
          <>
            {typeof data.productInfo.description === 'string' ? (
              <p>{data.productInfo.description}</p>
            ) : (
              <div className='[&>*:last-child]:!mb-0'>{data.productInfo.description}</div>
            )}
          </>
        )}
      </div>

      {loading ? (
        <div className='flex flex-row items-baseline gap-3'>
          <h4 className='h4'>
            <LoadingText textLength={6} />
          </h4>
        </div>
      ) : (
        <>
          {optionsForSelect.length > 0 && data.currentVariant && (
            <div className='flex flex-col flex-wrap gap-4 xl:flex-row'>
              {optionsForSelect.map(({ id, values, name }) => (
                <Select
                  key={id}
                  value={
                    data.selectSelectedOptions[name] ?? data.currentVariant?.selectedOptions[name]
                  }
                  onSelect={value => {
                    data.onSelectedOptionChange(
                      { [name]: value } as GrouppedOptionsByName,
                      'select',
                    )
                  }}
                  options={values.map(value => ({
                    title: value,
                    value,
                  }))}
                  selectProps={{
                    // @ts-ignore
                    ['data-testid']: 'product-variant-select',
                    id: `optionSelect-${id.split('/').at(-1)}`,
                  }}
                  selectClassName='gap-4 w-48 xl:w-60'
                  onInit={value => {
                    data.onSelectedOptionChange(
                      { [name]: value } as GrouppedOptionsByName,
                      'select',
                    )
                  }}
                />
              ))}
            </div>
          )}

          {tags.length > 0 && data.currentVariant && (
            <TagList
              itemClassName={
                //hide tag list for now while valid tags for select are still loading
                optionsForSelect.length > 0 && data.tagListOptions.length === 0
                  ? 'invisible'
                  : 'visible'
              }
              selectionMode='single'
              data={{
                tags,
                value: tagStartValue,
                onSelect: onChangeTagValue,
                onInit: onChangeTagValue,
                slider: mounted && !isDesktop,
              }}
              roundedTag={false}
              listClassName={mounted && !isDesktop ? 'mx-[-1.25rem]' : 'flex flex-wrap gap-2'}
            />
          )}

          {data.currentVariant && (
            <div className='flex flex-row items-baseline gap-3'>
              <h4
                className='h4'
                data-testid='product-price'
                aria-label={`${data.accessibility?.price} ${data.currentVariant.price}`}
              >
                {data.currentVariant.price}
              </h4>
              {data.currentVariant.compareAtPrice && (
                <h6
                  data-testid='compare-at-price'
                  className='text-darkgray line-through'
                  aria-label={`${data.accessibility?.compareAtPrice} ${data.currentVariant.compareAtPrice}`}
                >
                  {data.currentVariant.compareAtPrice}
                </h6>
              )}
            </div>
          )}
        </>
      )}

      <div className='flex flex-col gap-8 xl:gap-10'>
        <div className='flex w-full gap-2' id='shopButton'>
          <Select
            value={data?.quantity}
            onSelect={data?.onCountChange}
            options={Array.from({ length: 4 }, (_, index) => ({
              value: index + 1,
              title: index + 1,
            }))}
            selectClassName='w-[83px] xl:w-[96px]'
            selectProps={{
              id: 'countSelect',
              // @ts-ignore
              ['data-testid']: 'product-quantity-select',
            }}
          />

          <div className='w-full max-w-[440px] xl:max-w-none'>
            <Button
              onPress={data?.onAddItemToCheckout}
              data-testid='product-add-to-basket'
              isDisabled={loading || data?.isAddButtonDisabled}
              isLoading={loading}
              appearance='primary'
              className='h-full w-full !max-w-full lg:rounded-sm'
            >
              {data?.addToCartButtonText}
            </Button>
          </div>
        </div>

        {(data?.buyNowButtonText != null || data?.shopPay != null) && (
          <div className='flex w-full items-stretch gap-3'>
            {data?.buyNowButtonText != null && (
              <div className='flex-1'>
                <BuyNowButton
                  label={data.buyNowButtonText}
                  onPress={data?.onBuyNow}
                  isDisabled={loading || data?.isAddButtonDisabled}
                  isLoading={loading}
                />
              </div>
            )}

            {data?.shopPay != null && (
              <div className='flex-1'>
                <ShopPayCheckoutButton {...data.shopPay} />
              </div>
            )}
          </div>
        )}

        {loading ? (
          <p className='sm'>
            <LoadingText className='w-[60%]' />
          </p>
        ) : (
          <>
            {!loading && data.helperText != null && (
              <p data-testid='product-unit-price' className='sm text-darkgray'>
                {data.helperText}
              </p>
            )}
          </>
        )}

        {!loading && !data.isProductAvailable && data.currentVariant && (
          <>
            <motion.div
              animate={{
                opacity: data.itemIsOutOfStockFormSuccessMessage == null ? 0 : 1,
                height: data.itemIsOutOfStockFormSuccessMessage == null ? 0 : 'auto',
                transition: {
                  duration: 0.3,
                  ease: 'easeInOut',
                },
              }}
              data-testid='product-notification-outputs'
            >
              <p className='whitespace-pre-line'>{data.itemIsOutOfStockFormSuccessMessage}</p>
            </motion.div>
            <motion.div
              animate={{
                opacity: data.itemIsOutOfStockFormSuccess ? 0 : 1,
                height: data.itemIsOutOfStockFormSuccess ? 0 : 'auto',
                transition: {
                  duration: 0.3,
                  ease: 'easeInOut',
                },
              }}
              className='flex flex-col gap-4'
              data-testid='product-notification-inputs'
            >
              <div className='[&>*:first-child]:!mt-0 [&>*:last-child]:!mb-0'>
                {data.emailNotificationDescription}
              </div>
              <Input
                label={data.itemIsOutOfStockEmailNotificationInput}
                type='email'
                value={subscribeEmail}
                onChange={e => setSubscribeEmail(e)}
              />
              <Checkbox
                text={data.itemIsOutOfStockEmailNotificationNewsletterCheckboxText}
                checked={enableNewsletter}
                onChange={() => setEnableNewsletter(e => !e)}
              />
              <Button
                onPress={() => data.onSubscribe(subscribeEmail, enableNewsletter)}
                appearance='dark'
                className='!max-w-full'
                isDisabled={!subscribeEmail}
              >
                {data.itemIsOutOfStockEmailNotificationButtonText}
              </Button>
            </motion.div>
          </>
        )}

        {!loading && data.shippingDetails != null && data.isProductAvailable && (
          <TagList
            data={{ tags: data.shippingDetails, tagsColor: 'white' }}
            listClassName='flex flex-col gap-3 xl:gap-4'
          />
        )}
      </div>

      {showStickyBuyBar && (
        <div
          ref={buyBarRef}
          className='fixed bottom-0 left-0 z-40 hidden w-full flex-col gap-3 bg-white p-5 shadow-[0_-8px_12px_0_rgba(0,0,0,0.05)]'
        >
          <div className='flex w-full items-center justify-between gap-6'>
            {loading ? (
              <h6 className='h6 min-w-fit basis-0'>
                <LoadingText className='w-[40%]' />
              </h6>
            ) : (
              <>
                {data.currentVariant && (
                  <h6 className='h6 min-w-fit basis-0'>{data.currentVariant.price}</h6>
                )}
              </>
            )}

            <div className='w-full max-w-[440px]'>
              <Button
                onPress={data?.onAddItemToCheckout}
                data-testid='product-add-to-basket'
                isDisabled={loading || data?.isAddButtonDisabled}
                isLoading={loading}
                appearance='primary'
                className='h-full w-full !max-w-full lg:rounded-sm'
              >
                {data?.addToCartButtonText}
              </Button>
            </div>
          </div>

          {(data?.buyNowButtonText != null || data?.shopPay != null) && (
            <div className='flex w-full items-stretch gap-3'>
              {data?.buyNowButtonText != null && (
                <div className='flex-1'>
                  <BuyNowButton
                    label={data.buyNowButtonText}
                    onPress={data?.onBuyNow}
                    isDisabled={loading || data?.isAddButtonDisabled}
                    isLoading={loading}
                  />
                </div>
              )}

              {data?.shopPay != null && (
                <div className='flex-1'>
                  <ShopPayCheckoutButton {...data.shopPay} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!loading && (
        <div className='xl:pt-6'>
          <AccordionList
            initialOpenIndex={0}
            panels={data.productInfo.accordion}
            shouldScrollToFirstAccordion
          />
        </div>
      )}
    </div>
  )
}
